import {
  Button,
  DrawerHeader,
  DrawerBody,
  DrawerContent,
  Drawer,
  Divider,
  DrawerOverlay,
  Flex,
  VStack,
  HStack,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { FullDateRangePicker } from 'packages/common-frontend/components/DateRangePicker';
import { Icon } from 'packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { Typography } from 'packages/common-frontend/components/Typography';

type DateRange = {
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
};

export const SettlementReportGenerateSideDrawer = ({
  isOpen,
  onClose,
  loanOptions,
  lenderOptions,
  onClickGenerate,
  isCreateBatchSettlementLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  loanOptions: { id: string; label: string; value: string }[];
  lenderOptions: { id: string; label: string; value: string }[];
  onClickGenerate: (params: {
    lenderIds: string[];
    loanIds: string[];
    startDate: Util.FullDateStr;
    endDate: Util.FullDateStr;
  }) => Promise<void>;
  isCreateBatchSettlementLoading: boolean;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = commonDate.dateToFullDateString(new Date(), clientTimeZone);
  const oneWeekAgo = commonDate.addDays(now, -7);

  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>(
    loanOptions.map((loan) => loan.id),
  );
  const [selectedCapitalProviderIds, setSelectedCapitalProviderIds] = useState<
    string[]
  >(lenderOptions.map((lender) => lender.id));
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: oneWeekAgo,
    endDate: now,
  });

  const onCloseSideDrawer = useCallback(() => {
    onClose();

    // reset selected states
    setSelectedLoanIds(loanOptions.map((loan) => loan.id));
    setSelectedCapitalProviderIds(lenderOptions.map((lender) => lender.id));
    setSelectedDateRange({
      startDate: oneWeekAgo,
      endDate: now,
    });
  }, [onClose, oneWeekAgo, now, loanOptions, lenderOptions]);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="xl"
      // workaround for known chakra issue where drawer body is not scrollable
      // https://github.com/chakra-ui/chakra-ui/issues/1862
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader padding="0">
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              m="24px 24px 0 24px"
              p="1rem"
            >
              <Typography variant="section-title" trim>
                Generate Settlement Report
              </Typography>
              <Button variant="v2-ghost" onClick={onCloseSideDrawer}>
                <Icon icon="x" />
              </Button>
            </Flex>
            <Divider />
          </>
        </DrawerHeader>

        <DrawerBody p="40px">
          <Typography variant="base-muted">
            Reports will appear in the Settlement Reports tab. Processing may
            take up to a few minutes.
          </Typography>
          <VStack spacing={4} w="100%" alignItems="flex-start" mt="40px">
            <Typography fontWeight="bold">Lenders</Typography>
            <IsolatedMultiSelect
              applyButtonText="Apply"
              buttonProps={{
                width: '100%',
              }}
              initialSelections={selectedCapitalProviderIds}
              isSearchable
              menuListProps={{ zIndex: 'dropdown' }}
              onApplyOptions={(capitalProviderIds) => {
                setSelectedCapitalProviderIds(capitalProviderIds);
              }}
              options={lenderOptions}
              placeholderText="Select lenders"
              showSelectedCount
              titleText={`${
                selectedCapitalProviderIds.length === lenderOptions.length
                  ? 'All Lenders'
                  : 'Lenders'
              }`}
            />
            <Typography fontWeight="bold">Loans</Typography>
            <IsolatedMultiSelect
              applyButtonText="Apply"
              buttonProps={{
                width: '100%',
              }}
              initialSelections={selectedLoanIds}
              isSearchable
              menuListProps={{ zIndex: 'dropdown' }}
              onApplyOptions={(loanIds) => {
                setSelectedLoanIds(loanIds);
              }}
              options={loanOptions}
              placeholderText="Select loans"
              showSelectedCount
              titleText={`${
                selectedLoanIds.length === loanOptions.length
                  ? 'All Loans'
                  : 'Loans'
              }`}
            />
            <Typography fontWeight="bold">Date Range</Typography>
            <FullDateRangePicker
              onSelectDateRange={([newStartDate, newEndDate]) => {
                if (!newStartDate || !newEndDate) {
                  return;
                }

                setSelectedDateRange({
                  startDate: commonDate.dateToFullDateString(
                    newStartDate,
                    clientTimeZone,
                  ),
                  endDate: commonDate.dateToFullDateString(
                    newEndDate,
                    clientTimeZone,
                  ),
                });
              }}
              currentDateRange={{
                startDate: commonDate.fullDateStrToDate(
                  selectedDateRange.startDate,
                ), // defaults to clientTZ already
                endDate: commonDate.fullDateStrToDate(
                  selectedDateRange.endDate,
                ), // defaults to clientTZ already
              }}
              width="100%"
            />
          </VStack>
          <HStack w="100%" justifyContent="flex-end" mt="40px" spacing={8}>
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={onCloseSideDrawer}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              colorScheme="v2-aurora"
              isLoading={isCreateBatchSettlementLoading}
              onClick={async () => {
                await onClickGenerate({
                  lenderIds: selectedCapitalProviderIds,
                  loanIds: selectedLoanIds,
                  startDate: selectedDateRange.startDate,
                  endDate: selectedDateRange.endDate,
                });

                onCloseSideDrawer();
              }}
            >
              Generate
            </Button>
          </HStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
